import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router";
import { Plus } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { useAuth } from "../../../auth/hooks";
import type * as API from "@shared-core/data/api-types";
import { paths } from "@shared-core/routing/routes";
import { notifyTicketsChanged } from "@shared-core/domain/ticketEvents";
import { Button } from "../../primitives/button";
import { TicketCard } from "../tickets/TicketCard";
import { TicketModal } from "../tickets/TicketModal";
import { TodayNotesColumn } from "./TodayNotesColumn";
import { TODO_COLUMN_CLASS } from "./todoColumnLayout";
import { currentTime } from "@shared-core/domain/current-time";

type TodoFilter = "due_today" | "oldest" | "priority";

const FILTERS: { id: TodoFilter; label: string }[] = [
  { id: "due_today", label: "Due today" },
  { id: "oldest", label: "Oldest" },
  { id: "priority", label: "Priority" },
];

const PRIORITY_RANK: Record<API.TicketViewItem["priority"], number> = {
  urgent: 0,
  high: 1,
  normal: 2,
  low: 3,
};

function isDueToday(ticket: API.TicketViewItem): boolean {
  if (!ticket.due_date) return false;
  const due = new Date(ticket.due_date);
  const now = currentTime();
  return (
    due.getFullYear() === now.getFullYear() &&
    due.getMonth() === now.getMonth() &&
    due.getDate() === now.getDate()
  );
}

function sortTickets(
  rows: API.TicketViewItem[],
  filter: TodoFilter,
): API.TicketViewItem[] {
  const copy = [...rows];
  if (filter === "oldest") {
    return copy.sort(
      (a, b) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    );
  }
  if (filter === "priority") {
    return copy.sort(
      (a, b) => PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority],
    );
  }
  // due_today: due-today tickets first, then soonest due date.
  return copy.sort((a, b) => {
    const aToday = isDueToday(a) ? 0 : 1;
    const bToday = isDueToday(b) ? 0 : 1;
    if (aToday !== bToday) return aToday - bToday;
    if (!a.due_date) return 1;
    if (!b.due_date) return -1;
    return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
  });
}

const INITIAL_VISIBLE = 4;
const OPEN_STATUSES = ["open", "in_progress", "waiting"] as const;

// Subject-type views of the system-generated column. Marketing tickets pile
// up (some reps hold 50+ campaign ones), so these show a handful plus a link
// to the full list on the Tickets page rather than an in-place "show more".
interface TypeFilter {
  id: string;
  label: string;
  subjectTypes: string[];
  noun: string;
  ticketsLink: string;
}

const SYSTEM_TYPE_FILTERS: TypeFilter[] = [
  {
    id: "campaign",
    label: "Campaign",
    // campaign_engaged tickets raised before one-ticket-per-contact stay
    // open until worked, so both are listed.
    subjectTypes: ["campaign_contact", "campaign_engaged"],
    noun: "campaign",
    ticketsLink: `${paths.tickets()}?scope=mine&campaign_open=1&campaign_type=campaign_contact`,
  },
  {
    id: "landing_page",
    label: "Landing page",
    subjectTypes: ["landing_page_lead"],
    noun: "landing page",
    ticketsLink: `${paths.tickets()}?scope=mine&campaign_open=1&campaign_type=landing_page_lead`,
  },
];

function TicketColumn({
  title,
  subtitle,
  rows,
  loading,
  emptyMessage,
  typeFilters = [],
  userId,
  action,
  seeAllPrefix = "",
  allTicketsLink,
}: {
  title: string;
  subtitle: string;
  rows: API.TicketViewItem[];
  loading: boolean;
  emptyMessage: string;
  typeFilters?: TypeFilter[];
  userId?: string;
  action?: React.ReactNode;
  // Words between "See all N" and "tickets" for the unfiltered views, e.g.
  // "system generated ".
  seeAllPrefix?: string;
  // The Tickets page filtered to this column's tickets, for its unfiltered
  // "See all" link.
  allTicketsLink: string;
}) {
  const dataAdapter = useDataAdapter();
  const [filter, setFilter] = useState<string>("due_today");
  const [typed, setTyped] = useState<{
    items: API.TicketViewItem[];
    total: number;
  } | null>(null);
  const [typedLoading, setTypedLoading] = useState(false);

  const activeType = typeFilters.find((t) => t.id === filter);

  useEffect(() => {
    if (!activeType || !userId) {
      setTyped(null);
      return;
    }
    let cancelled = false;
    setTypedLoading(true);
    dataAdapter
      .getTicketViews({
        assignedToUserId: [userId],
        status: [...OPEN_STATUSES],
        subjectType: activeType.subjectTypes,
        pageSize: INITIAL_VISIBLE,
      })
      .then((r) => {
        if (!cancelled) setTyped({ items: r.items, total: r.total });
      })
      .catch(() => {
        if (!cancelled) setTyped({ items: [], total: 0 });
      })
      .finally(() => {
        if (!cancelled) setTypedLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter, activeType, userId]);

  const sorted = sortTickets(
    rows,
    (activeType ? "due_today" : filter) as TodoFilter,
  );
  const visible = activeType
    ? (typed?.items ?? [])
    : sorted.slice(0, INITIAL_VISIBLE);
  const totalCount = activeType ? (typed?.total ?? 0) : rows.length;
  const seeAllLink = activeType ? activeType.ticketsLink : allTicketsLink;
  const seeAllWords = `${activeType ? `${activeType.noun} ` : seeAllPrefix}${
    totalCount === 1 ? "ticket" : "tickets"
  }`;
  const isLoading = loading || (activeType ? typedLoading || !typed : false);
  const isEmpty = activeType ? (typed?.total ?? 0) === 0 : rows.length === 0;

  return (
    <div className={TODO_COLUMN_CLASS}>
      <h3 className="text-base mb-1">
        {title}{" "}
        <span className="text-sm text-muted-foreground">({rows.length})</span>
      </h3>
      <p className="text-xs text-muted-foreground mb-3">{subtitle}</p>
      <div className="flex gap-1.5 flex-wrap mb-3 content-start">
        {[...FILTERS, ...typeFilters].map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setFilter(f.id)}
            className={`px-2.5 py-0.5 rounded-full text-xs transition-colors ${
              filter === f.id
                ? "bg-primary text-white"
                : "bg-surface border border-border text-foreground hover:bg-surface-hover"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>
      <div className="mb-3">{action}</div>
      <div>
        {isLoading ? (
          <div className="text-muted-foreground text-sm p-6 text-center border border-border rounded-lg">
            Loading...
          </div>
        ) : isEmpty ? (
          <div className="text-muted-foreground text-sm p-6 text-center border border-border rounded-lg">
            {activeType ? `No ${activeType.noun} tickets` : emptyMessage}
          </div>
        ) : (
          <div className="space-y-2">
            {visible.map((ticket) => (
              <TicketCard
                key={ticket.id}
                ticket={ticket}
                customerName={ticket.customer_name ?? undefined}
                compact
                dense
              />
            ))}
            {totalCount > 0 && (
              <Link
                to={seeAllLink}
                className="text-sm text-primary hover:underline inline-block"
              >
                See all {totalCount} {seeAllWords}
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Consolidated to-do list — replaces the sales dashboard's former separate
 * Alerts and Opportunities columns with one unified, capped view of the
 * signed-in user's open tickets, split into human-set vs system-generated.
 * Alerts/Opportunities keep their own pages and convert-to-ticket actions
 * unchanged elsewhere in the app — only this dashboard's presentation
 * consolidates (product brief §5, point 3).
 */
export function DashboardToDoSection() {
  const dataAdapter = useDataAdapter();
  const { user } = useAuth();
  const [tickets, setTickets] = useState<API.TicketViewItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddTicket, setShowAddTicket] = useState(false);
  const [isSubmittingTicket, setIsSubmittingTicket] = useState(false);

  const loadTickets = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const result = await dataAdapter.getTicketViews({
        assignedToUserId: [user.id],
        status: [...OPEN_STATUSES],
        pageSize: 200,
      });
      setTickets(result.items);
    } catch {
      setTickets([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- adapter identity is stable in-app
  }, [user?.id]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  // Same creation path as the Tickets page's "Add ticket": no assignee means
  // the backend assigns the new ticket to the creator, so it lands in this
  // rep's own "Your tickets" column.
  const handleAddTicket = async (data: {
    title: string;
    description: string;
    actionType: "call" | "email" | "meeting" | "general";
    dueDate: string;
    priority: "low" | "normal" | "high" | "urgent";
    categoryId: string | null;
    customerId: string | null;
    assignedToUserId: string | null;
    departmentSlug: string | null;
    customerPersonId: string | null;
    subjectType: "alert" | "opportunity" | "job" | "estimate" | null;
    subjectId: string | null;
    subjectLabel: string | null;
  }) => {
    setIsSubmittingTicket(true);
    try {
      await dataAdapter.createTicket({
        title: data.title,
        description: data.description || undefined,
        due_date: data.dueDate || undefined,
        action_type: data.actionType,
        priority: data.priority,
        category_id: data.categoryId ?? undefined,
        customer_id: data.customerId ?? undefined,
        assigned_to_user_id: data.departmentSlug
          ? null
          : (data.assignedToUserId ?? undefined),
        department_slug: data.departmentSlug ?? undefined,
        customer_person_id: data.customerPersonId ?? undefined,
        subject_type: data.subjectType ?? undefined,
        subject_id: data.subjectId ?? undefined,
        subject_label: data.subjectLabel ?? undefined,
      });
      setShowAddTicket(false);
      notifyTicketsChanged();
      await loadTickets();
    } catch (error) {
      console.error("Error creating ticket:", error);
    } finally {
      setIsSubmittingTicket(false);
    }
  };

  const todoTickets = tickets.filter((t) => !t.is_system_generated);
  const systemTickets = tickets.filter((t) => t.is_system_generated);

  return (
    <div className="space-y-3">
      <h2 className="text-lg">To do list</h2>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-x-6 gap-y-6 lg:gap-y-0">
        <TicketColumn
          title="Your tickets"
          subtitle="Follow-ups you've set for yourself, plus tickets colleagues have assigned to you"
          rows={todoTickets}
          loading={loading}
          emptyMessage="Nothing on your list right now"
          allTicketsLink={`${paths.tickets()}?scope=mine&source=person`}
          action={
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowAddTicket(true)}
            >
              <Plus className="size-3.5" />
              Add a ticket
            </Button>
          }
        />
        <TicketColumn
          title="System generated"
          subtitle="These are your tickets created automatically by the system"
          rows={systemTickets}
          loading={loading}
          emptyMessage="No system-generated tickets"
          typeFilters={SYSTEM_TYPE_FILTERS}
          userId={user?.id}
          seeAllPrefix="system generated "
          // Opens the campaign box too: the main list leaves campaign
          // tickets to it, and this column's count includes them.
          allTicketsLink={`${paths.tickets()}?scope=mine&source=system&campaign_open=1`}
        />
        <TodayNotesColumn />
      </div>
      {showAddTicket && (
        <TicketModal
          onClose={() => setShowAddTicket(false)}
          onSave={handleAddTicket}
          isSubmitting={isSubmittingTicket}
        />
      )}
    </div>
  );
}
